import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import {
  ModelCreateActions,
  ModelCreateActionsProps,
  ModelCreateActionCancel,
  ModelCreateActionSave,
  ModelCreateActionSaveAnother,
  ModelCreateActionSaveShow
} from './ModelCreateActions';
import { useCallback, useMemo } from 'react';
import { ModalFooter } from '@heroui/react';

interface ModelCreateModalActionProps {
  onModalClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?: (data: any) => void;
  onCancel?: () => void;
}

export const ModelCreateModalActionSave = ({
  onModalClose,
  onSave
}: ModelCreateModalActionProps) => {
  const handleSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (onSave) onSave(data);

      onModalClose();
    },
    [onModalClose, onSave]
  );

  return <ModelCreateActionSave onSave={handleSave} />;
};

export const ModelCreateModalActionSaveShow = ({
  onModalClose,
  onSave
}: ModelCreateModalActionProps) => {
  const handleSave = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => {
      if (onSave) onSave(data);

      onModalClose();
    },
    [onModalClose, onSave]
  );

  return <ModelCreateActionSaveShow onSave={handleSave} />;
};

// No need to close the modal after saving another
export const ModelCreateModalActionSaveAnother = (
  props: ModelCreateModalActionProps
) => <ModelCreateActionSaveAnother {...props} />;

export const ModelCreateModalActionCancel = ({
  onModalClose,
  onCancel
}: ModelCreateModalActionProps) => {
  const handleCancel = useCallback(() => {
    if (onCancel) onCancel();

    onModalClose();
  }, [onModalClose, onCancel]);

  return <ModelCreateActionCancel onCancel={handleCancel} />;
};

const defaultComponents = {
  ModelCreateModalActionSave,
  ModelCreateModalActionCancel
};

export interface ModelCreateModalActionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  onModalClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?: (data: any) => void;
  [key: string]: unknown;
}

export const ModelCreateModalActionsBase = ({
  overrides,
  onModalClose,
  onSave,
  ...props
}: ModelCreateModalActionsProps) => {
  const { ModelCreateModalActionSave, ModelCreateModalActionCancel } =
    useOverrides(
      defaultComponents,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      overrides as Record<string, any>
    );

  const computedOverrides = useMemo(
    () => ({
      ModelCreateActionSave: {
        component: ModelCreateModalActionSave,
        props: { onModalClose, onSave }
      },
      ModelCreateActionCancel: {
        component: ModelCreateModalActionCancel,
        props: { onModalClose }
      }
    }),
    [
      ModelCreateModalActionSave,
      ModelCreateModalActionCancel,
      onModalClose,
      onSave
    ]
  );

  return (
    <ModalFooter>
      <ModelCreateActions
        style={{ gap: 5 }}
        overrides={computedOverrides}
        {...props}
      />
    </ModalFooter>
  );
};

export const ModelCreateModalActionsSaveShow = (
  props: ModelCreateActionsProps
) => (
  <ModelCreateActions
    overrides={{ ModelCreateActionSave: ModelCreateModalActionSaveShow }}
    {...props}
  />
);

export const ModelCreateModalActions = (props: ModelCreateModalActionsProps) =>
  useGlobalComponentForModel(
    'ModelCreateModalActions',
    ModelCreateModalActionsBase,
    props
  );
